<template>
  <div>
    <div class="container mx-auto my-8 p-6 bg-white rounded-lg">
      <div
        class="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6"
      >
        <h2 class="text-lg font-semibold text-bodyText">Project Resources</h2>
        <div class="flex flex-wrap gap-2">
          <button
            @click="openUploadModal"
            class="bg-primaryDarkBg text-white px-4 py-2 rounded-[6px] text-[14px] font-medium hover:bg-opacity-90 transition"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4 inline mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 4v16m8-8H4"
              />
            </svg>
            Upload File
          </button>
          <button
            @click="openCreateFolderModal"
            class="bg-grayLight text-bodyText px-4 py-2 rounded-[6px] text-[14px] font-medium hover:bg-gray-200 transition"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4 inline mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 6v6m0 0v6m0-6h6m-6 0H6"
              />
            </svg>
            Create Folder
          </button>
          <button
            v-if="canGoUp"
            @click="goUp"
            class="bg-gray-100 text-bodyText px-4 py-2 rounded-[6px] text-[14px] font-medium hover:bg-gray-200 transition"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4 inline mr-2"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M7 16l-4-4m0 0l4-4m-4 4h18"
              />
            </svg>
            Up
          </button>
        </div>
      </div>

      <!-- Breadcrumb Navigation -->
      <nav class="mb-4">
        <div class="flex items-center space-x-1 text-[13px]">
          <button
            @click="navigateToPath('')"
            class="text-blue-600 hover:text-blue-800 transition"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-4 w-4 inline mr-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z"
              />
            </svg>
            Root
          </button>
          <span
            v-for="(crumb, index) in breadcrumbs"
            :key="index"
            class="flex items-center"
          >
            <span class="text-gray-400 mx-1">/</span>
            <button
              @click="navigateToPath(crumb.path)"
              class="text-blue-600 hover:text-blue-800 transition capitalize"
            >
              {{ crumb.name }}
            </button>
          </span>
        </div>
      </nav>

      <!-- Loader -->
      <div v-if="loading" class="flex justify-center py-8">
        <div
          class="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primaryDarkBg"
        ></div>
      </div>

      <!-- No Items Message -->
      <div
        v-else-if="directories.length === 0 && files.length === 0"
        class="text-center text-bodyText"
      >
        <p
          class="mx-auto bg-primaryLight px-2 py-24 border border-grayLight rounded-[16px]"
        >
          No files or folders available in this directory.
        </p>
      </div>

      <!-- Files and Directories Table -->
      <div v-else class="space-y-6">
        <div
          class="overflow-x-auto min-h-[500px] max-h-[70vh] overflow-y-auto border border-gray-200 rounded-lg"
        >
          <div class="min-w-full inline-block align-middle">
            <table class="min-w-full divide-y table-auto">
              <thead class="text-[12px] text-bodyText">
                <tr>
                  <th
                    scope="col"
                    class="px-3 sm:px-6 py-3 text-left tracking-wider w-2/5"
                  >
                    Name
                  </th>
                  <th
                    scope="col"
                    class="px-3 sm:px-6 py-3 text-left tracking-wider hidden sm:table-cell"
                  >
                    Type
                  </th>
                  <th
                    scope="col"
                    class="px-3 sm:px-6 py-3 text-left tracking-wider hidden md:table-cell"
                  >
                    Size
                  </th>
                  <th
                    scope="col"
                    class="px-3 sm:px-6 py-3 text-left tracking-wider hidden lg:table-cell"
                  >
                    Modified
                  </th>
                  <th
                    scope="col"
                    class="px-3 sm:px-6 py-3 text-left tracking-wider hidden xl:table-cell"
                  >
                    Created By
                  </th>
                  <th
                    scope="col"
                    class="px-3 sm:px-6 py-3 text-left tracking-wider w-16"
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white divide-y divide-gray-200 text-[13px]">
                <!-- Directories -->
                <tr
                  v-for="directory in directories"
                  :key="`dir-${directory.path}`"
                  @click="selectDirectory(directory)"
                  @dblclick="navigateToPath(directory.path)"
                  class="border-t hover:bg-gray-50 transition text-grayDark cursor-pointer"
                  :class="{
                    'bg-blue-50 border-blue-200':
                      selectedDirectory?.path === directory.path,
                  }"
                >
                  <td class="flex gap-3 px-3 sm:px-6 py-4 whitespace-nowrap">
                    <div
                      class="w-[42px] h-[42px] flex items-center justify-center rounded-full text-white font-semibold bg-yellow-500"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z"
                        />
                      </svg>
                    </div>
                    <div class="flex flex-col justify-center min-w-0">
                      <p class="font-medium text-bodyText truncate">
                        {{ directory.name }}
                      </p>
                      <p class="text-[11px] text-gray-500 sm:hidden">
                        Folder • {{ formatDate(directory.last_modified) }}
                      </p>
                    </div>
                  </td>
                  <td class="py-4 whitespace-nowrap hidden sm:table-cell">
                    Folder
                  </td>
                  <td class="py-4 whitespace-nowrap hidden md:table-cell">-</td>
                  <td class="py-4 whitespace-nowrap hidden lg:table-cell">
                    {{ formatDate(directory.last_modified) }}
                  </td>
                  <td class="py-4 whitespace-nowrap hidden xl:table-cell">-</td>
                  <td class="py-4 whitespace-nowrap relative">
                    <div v-if="canDeleteDirectory()" class="relative">
                      <button
                        @click.stop="toggleDirectoryDropdown(directory.path)"
                        class="text-gray-500 hover:text-gray-700 transition p-2 rounded-full hover:bg-gray-100"
                        title="Folder Options"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          class="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                          />
                        </svg>
                      </button>

                      <div
                        v-if="activeDirectoryDropdown === directory.path"
                        class="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200"
                        @click.stop
                      >
                        <div class="py-1">
                          <button
                            @click="
                              navigateToPath(directory.path);
                              closeDirectoryDropdown();
                            "
                            class="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition"
                          >
                            <svg
                              class="h-4 w-4 mr-3 text-blue-600"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
                              />
                            </svg>
                            Open Folder
                          </button>

                          <button
                            @click="
                              confirmDeleteDirectory(directory);
                              closeDirectoryDropdown();
                            "
                            class="flex items-center w-full px-4 py-2 text-sm text-red-700 hover:bg-red-50 transition"
                          >
                            <svg
                              class="h-4 w-4 mr-3 text-red-600"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                            Delete Folder
                          </button>
                        </div>
                      </div>
                    </div>

                    <button
                      v-else
                      @click.stop="navigateToPath(directory.path)"
                      class="text-blue-600 hover:text-blue-800 transition cursor-pointer hover:bg-blue-100 p-2 rounded-full"
                      title="Open Folder"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </button>
                  </td>
                </tr>
                <!-- Files -->
                <tr
                  v-for="file in files"
                  :key="`file-${file.id}`"
                  class="border-t hover:bg-gray-50 transition text-grayDark"
                >
                  <td class="flex gap-3 px-3 sm:px-6 py-4 whitespace-nowrap">
                    <div
                      class="w-[42px] h-[42px] flex items-center justify-center rounded-full text-white font-semibold"
                      :class="getFileTypeColor(file.content_type)"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="2"
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                        />
                      </svg>
                    </div>

                    <div class="flex flex-col justify-center min-w-0">
                      <p class="font-medium text-bodyText truncate">
                        {{ file.name }}
                      </p>
                      <p
                        v-if="file.description"
                        class="text-[11px] text-gray-500 truncate"
                      >
                        {{ file.description }}
                      </p>
                      <p class="text-[11px] text-gray-500 sm:hidden">
                        {{ getFileType(file.content_type) }} •
                        {{ formatFileSize(file.size) }} •
                        {{ formatDate(file.last_modified || file.uploaded_at) }}
                      </p>
                      <div
                        v-if="file.tags && file.tags.length > 0"
                        class="flex gap-1 mt-1 flex-wrap"
                      >
                        <span
                          v-for="tag in file.tags.slice(0, 2)"
                          :key="tag"
                          class="px-2 py-0.5 bg-gray-100 text-gray-600 text-[10px] rounded-[4px]"
                        >
                          {{ tag }}
                        </span>
                        <span
                          v-if="file.tags.length > 2"
                          class="px-2 py-0.5 bg-gray-100 text-gray-600 text-[10px] rounded-[4px]"
                        >
                          +{{ file.tags.length - 2 }}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td class="py-4 whitespace-nowrap hidden sm:table-cell">
                    {{ getFileType(file.content_type) }} ({{
                      getFileExtension(file.name)
                    }})
                  </td>
                  <td class="py-4 whitespace-nowrap hidden md:table-cell">
                    {{ formatFileSize(file.size) }}
                  </td>
                  <td class="py-4 whitespace-nowrap hidden lg:table-cell">
                    {{ formatDate(file.last_modified || file.uploaded_at) }}
                  </td>
                  <td class="py-4 whitespace-nowrap hidden xl:table-cell">
                    {{ file.uploader_name || "Unknown" }}
                  </td>
                  <td class="py-4 whitespace-nowrap relative">
                    <div class="relative">
                      <button
                        @click="toggleFileDropdown(file.id)"
                        class="text-gray-500 hover:text-gray-700 transition p-2 rounded-full hover:bg-gray-100"
                        title="File Options"
                      >
                        <svg v-if="openingFileId === file.id || collabLoadingFileId === file.id" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582M20 20v-5h-.581M4 20v-5h.582M20 4v5h-.581"/>
                        </svg>
                        <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z"
                          />
                        </svg>
                      </button>

                      <!-- Dropdown Menu -->
                      <div
                        v-if="activeDropdown === file.id"
                        class="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200"
                        @click.stop
                      >
                        <div class="py-1">
                          <button
                            @click="open(file)"
                            class="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition"
                          >
                            <svg
                              class="h-4 w-4 mr-3 text-blue-600"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                              />
                              <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                              />
                            </svg>
                            <span>
                              <svg
                                v-if="collabLoadingFileId === file.id"
                                xmlns="http://www.w3.org/2000/svg"
                                class="h-4 w-4 mr-2 inline animate-spin"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                  stroke-width="2"
                                  d="M4 4v5h.582M20 20v-5h-.581M4 20v-5h.582M20 4v5h-.581"
                                />
                              </svg>
                              Open
                            </span>
                          </button>

                          <button
                            @click="
                              downloadFile(file);
                              closeDropdown();
                            "
                            class="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition"
                          >
                            <svg
                              class="h-4 w-4 mr-3 text-blue-600"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M12 10v6m0 0l-4-4m4 4l4-4m5-8H7a2 2 0 00-2 2v14a2 2 0 002 2h10a2 2 0 002-2V6a2 2 0 00-2-2z"
                              />
                            </svg>
                            Download
                          </button>

                          <button
                            @click="
                              shareFile(file);
                              closeDropdown();
                            "
                            class="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition"
                          >
                            <svg
                              class="h-4 w-4 mr-3 text-green-600"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"
                              />
                            </svg>
                            Share
                          </button>

                          <button
                            @click="
                              editFile(file);
                              closeDropdown();
                            "
                            class="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 transition"
                          >
                            <svg
                              class="h-4 w-4 mr-3 text-yellow-600"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                              />
                            </svg>
                            Edit
                          </button>
                          <button
                            v-if="canDeleteFile(file)"
                            @click="
                              confirmDeleteFile(file);
                              closeDropdown();
                            "
                            class="flex items-center w-full px-4 py-2 text-sm text-red-700 hover:bg-red-50 transition"
                          >
                            <svg
                              class="h-4 w-4 mr-3 text-red-600"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <!-- Upload Modal -->
    <div
      v-if="showUploadModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    >
      <div class="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 class="text-lg font-semibold mb-4">Upload File</h3>

        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2"
              >Select File</label
            >
            <input
              type="file"
              ref="fileInput"
              @change="handleFileSelect"
              class="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primaryDarkBg file:text-white hover:file:bg-opacity-90"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2"
              >Upload Location</label
            >
            <div class="mb-2 p-2 bg-gray-50 rounded-md">
              <p class="text-sm text-gray-600">
                <strong>Current folder:</strong> {{ currentPath || "Root" }}
              </p>
            </div>

            <div class="relative">
              <input
                v-model="uploadForm.subfolder"
                type="text"
                :placeholder="currentPath || 'Upload to current folder'"
                class="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md focus:outline-none focus:ring-primaryDarkBg focus:border-primaryDarkBg"
                @input="onSubfolderInput"
                @focus="onInputFocus"
                @blur="hideDropdownWithDelay"
              />
              <button
                type="button"
                @click="toggleUploadDirectoryDropdown"
                class="absolute inset-y-0 right-0 flex items-center pr-3 hover:bg-gray-50 rounded-r-md"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-4 w-4 text-gray-400 transition-transform"
                  :class="{ 'rotate-180': showDirectoryDropdown }"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              <!-- Custom Dropdown -->
              <div
                v-if="showDirectoryDropdown"
                class="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-40 overflow-y-auto"
                @mousedown.prevent
              >
                <div
                  class="px-3 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100"
                  @click="selectUploadDirectory('')"
                >
                  📁 Current folder ({{ currentPath || "Root" }})
                </div>
                <div
                  v-for="directory in directories"
                  :key="directory.path"
                  class="px-3 py-2 hover:bg-gray-100 cursor-pointer"
                  @click="selectUploadDirectory(getDirectoryPath(directory))"
                >
                  📁 {{ directory.name }}
                </div>
                <div
                  v-if="directories.length === 0"
                  class="px-3 py-2 text-gray-500 text-sm"
                >
                  No subdirectories found
                </div>
              </div>
            </div>

            <p class="text-xs text-gray-500 mt-1">
              Type a new folder name or select from existing folders. New
              folders will be created automatically.
            </p>

            <!-- Final Path Display -->
            <div
              v-if="
                finalUploadPath && finalUploadPath !== (currentPath || 'Root')
              "
              class="mt-2 p-2 bg-blue-50 border border-blue-200 rounded-md"
            >
              <p class="text-sm text-blue-700">
                <strong>Upload to:</strong> {{ finalUploadPath }}
              </p>
            </div>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2"
              >Description</label
            >
            <textarea
              v-model="uploadForm.description"
              rows="3"
              placeholder="File description (optional)"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primaryDarkBg focus:border-primaryDarkBg"
            ></textarea>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2"
              >Tags</label
            >
            <input
              v-model="uploadForm.tags"
              type="text"
              placeholder="Comma-separated tags (optional)"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primaryDarkBg focus:border-primaryDarkBg"
            />
          </div>
        </div>

        <div v-if="uploadProgress > 0" class="mt-4">
          <div class="w-full bg-gray-200 rounded-full h-2">
            <div
              class="bg-primaryDarkBg h-2 rounded-full transition-all"
              :style="`width: ${uploadProgress}%`"
            ></div>
          </div>
          <p class="text-sm text-gray-600 mt-1">
            {{ uploadProgress }}% uploaded
          </p>
        </div>

        <div class="flex justify-end gap-3 mt-6">
          <button
            @click="closeUploadModal"
            :disabled="uploading"
            class="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            @click="uploadFile"
            :disabled="!uploadForm.file || uploading"
            class="px-4 py-2 bg-primaryDarkBg text-white rounded-md hover:bg-opacity-90 disabled:opacity-50"
          >
            {{ uploading ? "Uploading..." : "Upload" }}
          </button>
        </div>
      </div>
    </div>

    <!-- Edit File Modal -->
    <div
      v-if="showEditModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    >
      <div class="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 class="text-lg font-semibold mb-4">Edit File</h3>

        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2"
              >File Name</label
            >
            <input
              :value="editForm.name"
              type="text"
              disabled
              class="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2"
              >Description</label
            >
            <textarea
              v-model="editForm.description"
              rows="3"
              placeholder="File description (optional)"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primaryDarkBg focus:border-primaryDarkBg"
            ></textarea>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2"
              >Tags</label
            >
            <input
              v-model="editForm.tagsString"
              type="text"
              placeholder="Comma-separated tags (optional)"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primaryDarkBg focus:border-primaryDarkBg"
            />
          </div>
        </div>

        <div class="flex justify-end gap-3 mt-6">
          <button
            @click="closeEditModal"
            :disabled="updating"
            class="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            @click="updateFile"
            :disabled="updating"
            class="px-4 py-2 bg-primaryDarkBg text-white rounded-md hover:bg-opacity-90 disabled:opacity-50"
          >
            {{ updating ? "Saving..." : "Save Changes" }}
          </button>
        </div>
      </div>
    </div>

    <!-- Share Modal -->
    <div
      v-if="showShareModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    >
      <div class="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 class="text-lg font-semibold mb-4">Share File</h3>

        <div class="space-y-4">
          <div class="bg-gray-50 p-3 rounded-md">
            <p class="font-medium">{{ shareForm.filename }}</p>
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2">
              <input
                type="checkbox"
                v-model="shareForm.usePassword"
                class="mr-2"
              />
              Password Protection
            </label>
            <input
              v-if="shareForm.usePassword"
              v-model="shareForm.password"
              type="password"
              placeholder="Enter password"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primaryDarkBg focus:border-primaryDarkBg"
            />
          </div>

          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2"
              >Expires In (days)</label
            >
            <select
              v-model="shareForm.expireDays"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primaryDarkBg focus:border-primaryDarkBg"
            >
              <option value="">Never</option>
              <option value="1">1 day</option>
              <option value="7">7 days</option>
              <option value="30">30 days</option>
              <option value="90">90 days</option>
            </select>
          </div>

          <div v-if="shareForm.shareUrl" class="mt-4">
            <label class="block text-sm font-medium text-gray-700 mb-2"
              >Share Link</label
            >
            <div class="flex gap-2">
              <input
                :value="shareForm.shareUrl"
                type="text"
                readonly
                class="flex-1 px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
              />
              <button
                @click="copyToClipboard(shareForm.shareUrl)"
                class="px-3 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
              >
                Copy
              </button>
            </div>
          </div>
        </div>

        <div class="flex justify-end gap-3 mt-6">
          <button
            @click="closeShareModal"
            :disabled="generating"
            class="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
          >
            Close
          </button>
          <button
            v-if="!shareForm.shareUrl"
            @click="generateShareLink"
            :disabled="generating"
            class="px-4 py-2 bg-primaryDarkBg text-white rounded-md hover:bg-opacity-90 disabled:opacity-50"
          >
            {{ generating ? "Generating..." : "Generate Link" }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div
      v-if="showDeleteModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    >
      <div class="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 class="text-lg font-semibold mb-4 text-red-600">Delete File</h3>

        <p class="mb-4 text-gray-700">
          Are you sure you want to delete "<strong>{{
            deleteForm.filename
          }}</strong
          >"? This action cannot be undone.
        </p>

        <div class="flex justify-end gap-3">
          <button
            @click="closeDeleteModal"
            :disabled="deleting"
            class="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            @click="deleteFile"
            :disabled="deleting"
            class="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
          >
            {{ deleting ? "Deleting..." : "Delete" }}
          </button>
        </div>
      </div>
    </div>

    <!-- Create Folder Modal -->
    <div
      v-if="showCreateFolderModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    >
      <div class="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 class="text-lg font-semibold mb-4">Create New Folder</h3>

        <div class="space-y-4">
          <div>
            <label class="block text-sm font-medium text-gray-700 mb-2"
              >Folder Name</label
            >
            <input
              v-model="createFolderForm.name"
              type="text"
              placeholder="Enter folder name"
              class="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primaryDarkBg focus:border-primaryDarkBg"
              @keyup.enter="createFolder"
            />
          </div>

          <div class="bg-gray-50 p-3 rounded-md">
            <p class="text-sm text-gray-600">
              <strong>Create in:</strong> {{ currentPath || "Root" }}
            </p>
          </div>
        </div>

        <div class="flex justify-end gap-3 mt-6">
          <button
            @click="closeCreateFolderModal"
            :disabled="creatingFolder"
            class="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            @click="createFolder"
            :disabled="!createFolderForm.name.trim() || creatingFolder"
            class="px-4 py-2 bg-primaryDarkBg text-white rounded-md hover:bg-opacity-90 disabled:opacity-50"
          >
            {{ creatingFolder ? "Creating..." : "Create Folder" }}
          </button>
        </div>
      </div>
    </div>

    <div
      v-if="showDeleteFolderModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    >
      <div class="bg-white rounded-lg p-6 w-full max-w-md">
        <h3 class="text-lg font-semibold mb-4 text-red-600">Delete Folder</h3>

        <div class="space-y-4">
          <div class="bg-gray-50 p-3 rounded-md">
            <p class="font-medium text-gray-800">{{ deleteFolderForm.name }}</p>
            <p class="text-sm text-gray-600 mt-1">Path: {{ deleteFolderForm.path }}</p>
          </div>

          <div v-if="deleteFolderForm.isEmpty === false" class="bg-yellow-50 border border-yellow-200 rounded-md p-4">
            <div class="flex items-start">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-6 w-6 text-yellow-600 mr-3 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <div>
                <p class="font-semibold text-yellow-800">Warning: Folder Not Empty</p>
                <p class="text-sm text-yellow-700 mt-1">
                  This folder contains files and/or subdirectories. Deleting it will permanently remove all contents.
                </p>
              </div>
            </div>
          </div>

          <div v-else-if="deleteFolderForm.isEmpty === true" class="bg-blue-50 border border-blue-200 rounded-md p-4">
            <div class="flex items-start">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="h-6 w-6 text-blue-600 mr-3 flex-shrink-0"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <div>
                <p class="font-semibold text-blue-800">Empty Folder</p>
                <p class="text-sm text-blue-700 mt-1">
                  This folder is empty and can be safely deleted.
                </p>
              </div>
            </div>
          </div>

          <p class="text-gray-700">
            Are you sure you want to delete this folder<span v-if="deleteFolderForm.isEmpty === false"> and all its contents</span>? This action cannot be undone.
          </p>
        </div>

        <div class="flex justify-end gap-3 mt-6">
          <button
            @click="closeDeleteFolderModal"
            :disabled="deletingFolder"
            class="px-4 py-2 text-gray-600 hover:text-gray-800 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            @click="deleteDirectory"
            :disabled="deletingFolder"
            class="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
          >
            {{ deletingFolder ? "Deleting..." : "Delete Folder" }}
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import apiClient from "@/axios";

export default {
  name: "ResourcesComponent",
  components: {},
  props: {
    projectId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      currentPath: "",
      directories: [],
      files: [],
      breadcrumbs: [],
      navigationHistory: [""],
      loading: true,

      selectedDirectory: null,

      activeDropdown: null,
      activeDirectoryDropdown: null,

      showUploadModal: false,
      uploading: false,
      uploadProgress: 0,
      showDirectoryDropdown: false,
      dropdownTimeout: null,
  collabLoadingFileId: null,
  openingFileId: null,
      uploadForm: {
        file: null,
        subfolder: "",
        description: "",
        tags: "",
      },

      showEditModal: false,
      updating: false,
      editForm: {
        id: null,
        name: "",
        description: "",
        tags: [],
        tagsString: "",
      },

      showShareModal: false,
      generating: false,
      shareForm: {
        fileId: null,
        filename: "",
        usePassword: false,
        password: "",
        expireDays: "",
        shareUrl: "",
      },

      showDeleteModal: false,
      deleting: false,
      deleteForm: {
        fileId: null,
        filename: "",
      },

      showCreateFolderModal: false,
      creatingFolder: false,
      createFolderForm: {
        name: "",
      },

      showDeleteFolderModal: false,
      deletingFolder: false,
      deleteFolderForm: {
        path: "",
        name: "",
        isEmpty: null,
      },

      currentUser: null,

    };
  },
  computed: {
    isAdmin() {
      return this.$store.getters.isAdmin;
    },
    isManager() {
      return this.$store.getters.isManager;
    },
    isTalent() {
      return this.$store.getters.isTalent;
    },
    canGoUp() {
      return this.currentPath !== "";
    },
    finalUploadPath() {
      if (
        !this.uploadForm.subfolder ||
        this.uploadForm.subfolder === this.currentPath
      ) {
        return this.currentPath || "Root";
      }
      return this.uploadForm.subfolder;
    },
  },
  methods: {
    async browseDirectory(path = "") {
      try {
        this.loading = true;

        const params = {};
        if (path) {
          params.path = path;
        }

        const response = await apiClient.get(
          `/web/files/projects/${this.projectId}/browse`,
          {
            params,
          }
        );

        const data = response.data.data;
        this.currentPath = data.current_path || "";
        this.directories = data.directories || [];
        this.files = data.files || [];
        this.breadcrumbs = data.breadcrumbs || [];

        // Clear selection when navigating
        this.selectedDirectory = null;

        if (
          this.navigationHistory[this.navigationHistory.length - 1] !== path
        ) {
          this.navigationHistory.push(path);
        }
      } catch (error) {
        console.error("Error browsing directory:", error);
        this.showErrorMessage("Failed to browse directory");
      } finally {
        this.loading = false;
      }
    },

    selectDirectory(directory) {
      this.selectedDirectory = directory;
    },

    async navigateToPath(path) {
      await this.browseDirectory(path);
    },

    async goUp() {
      if (!this.canGoUp) return;

      const parentPath = this.currentPath.split("/").slice(0, -1).join("/");
      await this.browseDirectory(parentPath);
    },

    async goBack() {
      if (this.navigationHistory.length > 1) {
        this.navigationHistory.pop();
        const previousPath =
          this.navigationHistory[this.navigationHistory.length - 1];
        await this.browseDirectory(previousPath);
      }
    },

    async fetchFiles() {
      await this.browseDirectory(this.currentPath);
    },

    openCreateFolderModal() {
      this.showCreateFolderModal = true;
      this.createFolderForm.name = "";
    },

    closeCreateFolderModal() {
      this.showCreateFolderModal = false;
      this.createFolderForm.name = "";
    },

    async createFolder() {
      if (!this.createFolderForm.name.trim()) return;

      try {
        this.creatingFolder = true;

        const payload = {
          subfolder_name: this.createFolderForm.name.trim(),
          parent_folder: this.currentPath || "",
        };

        await apiClient.post(
          `/web/files/projects/${this.projectId}/folders`,
          payload
        );

        this.showSuccessMessage("Folder created successfully");
        this.closeCreateFolderModal();
        await this.browseDirectory(this.currentPath);
      } catch (error) {
        console.error("Error creating folder:", error);

        let errorMessage = "Failed to create folder";

        if (error.response?.data) {
          if (error.response.data.detail) {
            errorMessage = error.response.data.detail;
          } else if (error.response.data.message) {
            errorMessage = error.response.data.message;
          } else if (error.response.data.error) {
            errorMessage = error.response.data.error;
          } else if (typeof error.response.data === "string") {
            errorMessage = error.response.data;
          }
        } else if (error.message) {
          errorMessage = error.message;
        }

        if (error.response?.status === 409) {
          errorMessage =
            errorMessage ||
            "A folder with this name already exists in this location.";
        } else if (error.response?.status === 403) {
          errorMessage =
            "You don't have permission to create folders in this location.";
        }

        this.showErrorMessage(errorMessage);
      } finally {
        this.creatingFolder = false;
      }
    },

    openUploadModal() {
      this.showUploadModal = true;
      this.resetUploadForm();
    },

    closeUploadModal() {
      this.showUploadModal = false;
      this.resetUploadForm();
    },

    resetUploadForm() {
      this.uploadForm = {
        file: null,
        subfolder: this.currentPath || "",
        description: "",
        tags: "",
      };
      this.uploadProgress = 0;
      this.uploading = false;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = "";
      }
    },

    getDirectoryPath(directory) {
      // Return the full path for the directory
      return directory.path;
    },

    onSubfolderInput() {
      // This method can be used for any additional logic when subfolder input changes
      // Currently just reactive binding handles the updates
    },

    onInputFocus() {
      // Clear any pending timeout
      if (this.dropdownTimeout) {
        clearTimeout(this.dropdownTimeout);
        this.dropdownTimeout = null;
      }
      this.showDirectoryDropdown = true;
    },

    toggleUploadDirectoryDropdown() {
      // Clear any pending timeout
      if (this.dropdownTimeout) {
        clearTimeout(this.dropdownTimeout);
        this.dropdownTimeout = null;
      }
      this.showDirectoryDropdown = !this.showDirectoryDropdown;
    },

    selectUploadDirectory(path) {
      console.log("Selecting upload directory:", path); // Debug log
      this.uploadForm.subfolder = path;
      this.showDirectoryDropdown = false;
      // Force reactivity update
      this.$forceUpdate();
    },

    hideDropdownWithDelay() {
      // Add a small delay to allow clicking on dropdown items
      // Clear any existing timeout first
      if (this.dropdownTimeout) {
        clearTimeout(this.dropdownTimeout);
      }
      this.dropdownTimeout = setTimeout(() => {
        this.showDirectoryDropdown = false;
      }, 200);
    },

    handleFileSelect(event) {
      const file = event.target.files[0];
      this.uploadForm.file = file;
    },

    async uploadFile() {
      if (!this.uploadForm.file) return;

      try {
        this.uploading = true;
        this.uploadProgress = 0;

        const formData = new FormData();
        formData.append("file", this.uploadForm.file);

        if (this.uploadForm.subfolder.trim()) {
          formData.append("subfolder", this.uploadForm.subfolder.trim());
        }

        if (this.uploadForm.description.trim()) {
          formData.append("description", this.uploadForm.description.trim());
        }

        if (this.uploadForm.tags.trim()) {
          formData.append("tags", this.uploadForm.tags.trim());
        }

        await apiClient.post(
          `/web/files/projects/${this.projectId}/files/upload`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
            onUploadProgress: (progressEvent) => {
              this.uploadProgress = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
            },
          }
        );

        this.showSuccessMessage("File uploaded successfully");
        this.closeUploadModal();
        await this.browseDirectory(this.currentPath);
      } catch (error) {
        console.error("Error uploading file:", error);

        let errorMessage = "Failed to upload file";

        if (error.response?.data) {
          if (error.response.data.detail) {
            errorMessage = error.response.data.detail;
          } else if (error.response.data.message) {
            errorMessage = error.response.data.message;
          } else if (error.response.data.error) {
            errorMessage = error.response.data.error;
          } else if (typeof error.response.data === "string") {
            errorMessage = error.response.data;
          }
        } else if (error.message) {
          errorMessage = error.message;
        }

        if (error.response?.status === 413) {
          errorMessage =
            "File is too large to upload. Please choose a smaller file.";
        } else if (error.response?.status === 415) {
          errorMessage =
            "File type is not supported. Please choose a different file.";
        } else if (error.response?.status === 409) {
          errorMessage =
            errorMessage ||
            "A file with this name already exists in this location.";
        } else if (error.response?.status >= 500) {
          errorMessage = "Server error occurred. Please try again later.";
        }

        this.showErrorMessage(errorMessage);
      } finally {
        this.uploading = false;
      }
    },

    async downloadFile(file) {
      try {
        const response = await apiClient.get(
          `/web/files/projects/${this.projectId}/files/${file.id}/download`,
          {
            responseType: "blob",
          }
        );

        const contentType =
          response.headers["content-type"] ||
          file.content_type ||
          "application/octet-stream";

        const blob = new Blob([response.data], { type: contentType });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", file.name || file.filename || "download");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Error downloading file:", error);
        this.showErrorMessage("Failed to download file");
      }
    },

    editFile(file) {
      this.editForm = {
        id: file.id,
        name: file.name,
        description: file.description || "",
        tags: file.tags || [],
        tagsString: (file.tags || []).join(", "),
      };
      this.showEditModal = true;
    },

    closeEditModal() {
      this.showEditModal = false;
    },

    async updateFile() {
      try {
        this.updating = true;

        const updateData = {
          description: this.editForm.description.trim() || null,
          tags: this.editForm.tagsString
            ? this.editForm.tagsString.split(",").map((tag) => tag.trim())
            : [],
        };

        await apiClient.patch(
          `/web/files/projects/${this.projectId}/files/${this.editForm.id}`,
          updateData
        );

        this.showSuccessMessage("File updated successfully");
        this.closeEditModal();
        await this.browseDirectory(this.currentPath);
      } catch (error) {
        console.error("Error updating file:", error);
        this.showErrorMessage("Failed to update file");
      } finally {
        this.updating = false;
      }
    },

    shareFile(file) {
      this.shareForm = {
        fileId: file.id,
        filename: file.name,
        usePassword: false,
        password: "",
        expireDays: "",
        shareUrl: file.share_link || "",
      };
      this.showShareModal = true;
    },

    closeShareModal() {
      this.showShareModal = false;
      this.shareForm.shareUrl = "";
    },

    async generateShareLink() {
      try {
        this.generating = true;

        const shareData = {};
        if (this.shareForm.usePassword && this.shareForm.password) {
          shareData.password = this.shareForm.password;
        }
        if (this.shareForm.expireDays) {
          shareData.expire_days = parseInt(this.shareForm.expireDays);
        }

        const response = await apiClient.post(
          `/web/files/projects/${this.projectId}/files/${this.shareForm.fileId}/share`,
          shareData
        );

        this.shareForm.shareUrl = response.data.data.share_url;

        this.showSuccessMessage("Share link generated successfully");
      } catch (error) {
        console.error("Error generating share link:", error);
        let errorMessage = "Failed to generate share link.";
        if (this.shareForm.usePassword && this.shareForm.password) {
          errorMessage += " Use a more secured password.";
        }
        this.showErrorMessage(errorMessage);
      } finally {
        this.generating = false;
      }
    },

    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text);
        this.showSuccessMessage("Link copied to clipboard");
      } catch (error) {
        console.error("Failed to copy:", error);
      }
    },

    confirmDeleteFile(file) {
      this.deleteForm = {
        fileId: file.id,
        filename: file.name,
      };
      this.showDeleteModal = true;
    },

    closeDeleteModal() {
      this.showDeleteModal = false;
    },

    async deleteFile() {
      try {
        this.deleting = true;

        await apiClient.delete(
          `/web/files/projects/${this.projectId}/files/${this.deleteForm.fileId}`
        );

        this.showSuccessMessage("File deleted successfully");
        this.closeDeleteModal();
        await this.browseDirectory(this.currentPath);
      } catch (error) {
        console.error("Error deleting file:", error);

        let errorMessage = "Failed to delete file";

        if (error.response?.data) {
          if (error.response.data.detail) {
            errorMessage = error.response.data.detail;
          } else if (error.response.data.message) {
            errorMessage = error.response.data.message;
          } else if (error.response.data.error) {
            errorMessage = error.response.data.error;
          } else if (typeof error.response.data === "string") {
            errorMessage = error.response.data;
          }
        } else if (error.message) {
          errorMessage = error.message;
        }

        if (error.response?.status === 403) {
          errorMessage = "You don't have permission to delete this file.";
        } else if (error.response?.status === 404) {
          errorMessage = "File not found. It may have already been deleted.";
        } else if (error.response?.status === 409) {
          errorMessage =
            errorMessage ||
            "File cannot be deleted because it's currently in use.";
        }

        this.showErrorMessage(errorMessage);
      } finally {
        this.deleting = false;
      }
    },

    canDeleteFile(file) {
      if (this.isAdmin || this.isManager) return true;

      const currentUser = this.$store.state.user;
      return currentUser && file.uploaded_by === currentUser.user_id;
    },

    canDeleteDirectory() {
      return this.isAdmin || this.isManager;
    },

    async confirmDeleteDirectory(directory) {
      if (!directory || !directory.path) return;

      this.deleteFolderForm = {
        path: directory.path,
        name: directory.name,
        isEmpty: null,
      };

      try {
        const response = await apiClient.get(
          `/web/files/projects/${this.projectId}/folders/check-empty`,
          {
            params: { path: directory.path },
          }
        );

        const data = response.data.data;
        this.deleteFolderForm.isEmpty = data.is_empty;

        if (data.is_empty) {
          await this.deleteDirectory();
        } else {
          this.showDeleteFolderModal = true;
        }
      } catch (error) {
        console.error("Error checking if directory is empty:", error);
        
        let errorMessage = "Failed to check directory status";
        if (error.response?.data) {
          if (error.response.data.detail) {
            errorMessage = error.response.data.detail;
          } else if (error.response.data.message) {
            errorMessage = error.response.data.message;
          }
        }
        
        this.showErrorMessage(errorMessage);
      }
    },

    closeDeleteFolderModal() {
      this.showDeleteFolderModal = false;
      this.deleteFolderForm = {
        path: "",
        name: "",
        isEmpty: null,
      };
    },

    async deleteDirectory() {
      if (!this.deleteFolderForm.path) return;

      try {
        this.deletingFolder = true;

        await apiClient.delete(
          `/web/files/projects/${this.projectId}/folders`,
          {
            params: { path: this.deleteFolderForm.path },
          }
        );

        this.showSuccessMessage("Folder deleted successfully");
        this.closeDeleteFolderModal();
        await this.browseDirectory(this.currentPath);
      } catch (error) {
        console.error("Error deleting directory:", error);

        let errorMessage = "Failed to delete folder";

        if (error.response?.data) {
          if (error.response.data.detail) {
            errorMessage = error.response.data.detail;
          } else if (error.response.data.message) {
            errorMessage = error.response.data.message;
          } else if (error.response.data.error) {
            errorMessage = error.response.data.error;
          } else if (typeof error.response.data === "string") {
            errorMessage = error.response.data;
          }
        } else if (error.message) {
          errorMessage = error.message;
        }

        if (error.response?.status === 403) {
          errorMessage = "You don't have permission to delete this folder. Only managers can delete folders.";
        } else if (error.response?.status === 404) {
          errorMessage = "Folder not found. It may have already been deleted.";
        } else if (error.response?.status === 400) {
          errorMessage = errorMessage || "Invalid folder path or cannot delete root directory.";
        }

        this.showErrorMessage(errorMessage);
      } finally {
        this.deletingFolder = false;
      }
    },

    toggleFileDropdown(fileId) {
      this.activeDropdown = this.activeDropdown === fileId ? null : fileId;
    },

    closeDropdown() {
      this.activeDropdown = null;
    },

    toggleDirectoryDropdown(directoryPath) {
      this.activeDirectoryDropdown = 
        this.activeDirectoryDropdown === directoryPath ? null : directoryPath;
    },

    closeDirectoryDropdown() {
      this.activeDirectoryDropdown = null;
    },

    handleOutsideClick(event) {
      if (!event.target.closest(".relative")) {
        this.activeDropdown = null;
        this.activeDirectoryDropdown = null;
      }
    },

    formatFileSize(bytes) {
      if (bytes === 0) return "0 Bytes";

      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));

      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    },

    getFileExtension(filename) {
      if (!filename) return "FILE";
      const lastDot = filename.lastIndexOf(".");
      return lastDot !== -1
        ? filename.substring(lastDot + 1).toUpperCase()
        : "FILE";
    },

    getFileType(contentType) {
      if (!contentType) return "File";
      if (contentType.startsWith("image/")) return "Image";
      if (contentType.startsWith("video/")) return "Video";
      if (contentType.startsWith("audio/")) return "Audio";
      if (contentType.includes("pdf")) return "PDF";
      if (contentType.includes("word") || contentType.includes("document"))
        return "Document";
      if (contentType.includes("spreadsheet") || contentType.includes("excel"))
        return "Spreadsheet";
      if (
        contentType.includes("presentation") ||
        contentType.includes("powerpoint")
      )
        return "Presentation";
      if (
        contentType.includes("zip") ||
        contentType.includes("rar") ||
        contentType.includes("compressed")
      )
        return "Archive";
      return "File";
    },

    getFileTypeColor(contentType) {
      if (!contentType) return "bg-gray-500";
      if (contentType.startsWith("image/")) return "bg-blue-500";
      if (contentType.startsWith("video/")) return "bg-red-500";
      if (contentType.startsWith("audio/")) return "bg-purple-500";
      if (contentType.includes("pdf")) return "bg-red-600";
      if (contentType.includes("word") || contentType.includes("document"))
        return "bg-blue-600";
      if (contentType.includes("spreadsheet") || contentType.includes("excel"))
        return "bg-green-600";
      if (
        contentType.includes("presentation") ||
        contentType.includes("powerpoint")
      )
        return "bg-orange-600";
      if (
        contentType.includes("zip") ||
        contentType.includes("rar") ||
        contentType.includes("compressed")
      )
        return "bg-gray-600";
      return "bg-gray-500";
    },

    formatDate(dateString) {
      if (!dateString) return "-";

      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return "-";

        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch (error) {
        return "-";
      }
    },

    isSupportedForCollab(file) {
      if (!file || !file.content_type || !file.name) return false;
      const ext = this.getFileExtension(file.name).toLowerCase();
      const supported = ["DOC", "DOCX", "ODT", "XLSX", "PPTX", "XLS", "PPT"];
      return supported.includes(ext.toUpperCase());
    },

    async open(file) {
      if (!file) return;
      this.closeDropdown();
      this.openingFileId = file.id;
      try {
        if (this.isSupportedForCollab(file)) {
          await this.openCollaborativeEditor(file);
        } else {
          await this.openFile(file);
        }
      } finally {
        this.openingFileId = null;
      }
    },

    async openCollaborativeEditor(file) {
      if (!file || !file.id) return;
      try {
        this.collabLoadingFileId = file.id;

        // Try to get existing session first
        try {
          const getResp = await apiClient.get(
            `/web/files/projects/${this.projectId}/files/${file.id}/collaborate`
          );
          const existing = getResp.data?.data;
          if (existing?.edit_url) {
            window.open(existing.edit_url, "_blank");
            this.showSuccessMessage("Opened collaborative editor in a new tab");
            return;
          }
        } catch (err) {
          console.error("Error getting existing collaborative session:", err);
        }
        
        // Create a new collaborative session (default edit permissions)
        const payload = { permissions: "edit" };
        const resp = await apiClient.post(
          `/web/files/projects/${this.projectId}/files/${file.id}/collaborate`,
          payload
        );
        const data = resp.data?.data;
        if (data?.edit_url) {
          window.open(data.edit_url, "_blank");
          this.showSuccessMessage(
            "Collaborative session started and opened in a new tab"
          );
        } else {
          this.showErrorMessage(
            "Collaborative editor URL not returned by server."
          );
        }
      } catch (error) {
        console.error("Error opening collaborative editor:", error);
        let msg = "Failed to start collaborative editing session.";
        if (error.response?.data) {
          msg =
            error.response.data.message || error.response.data.detail || msg;
        }
        this.showErrorMessage(msg);
      } finally {
        this.collabLoadingFileId = null;
      }
    },

    async openFile(file) {
      if (!file || !file.id) return;
      try {
        const response = await apiClient.get(
          `/web/files/projects/${this.projectId}/files/${file.id}/download`,
          { responseType: "blob" }
        );

        const contentType =
          response.headers["content-type"] ||
          file.content_type ||
          "application/octet-stream";
        const blob = new Blob([response.data], { type: contentType });
        const url = window.URL.createObjectURL(blob);

        const newTab = window.open(url, "_blank");
        if (!newTab) {
          this.showErrorMessage(
            "Popup blocked. Please allow popups for this site to open files."
          );
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", file.name || "download");
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        } else {
          setTimeout(() => window.URL.revokeObjectURL(url), 10000);
        }
      } catch (error) {
        console.error("Error opening file:", error);
        this.showErrorMessage("Failed to open file");
      }
    },

    showSuccessMessage(message) {
      this.$toast.success(message);
    },

    showErrorMessage(message) {
      this.$toast.error(message);
    },
  },
  created() {
    if (this.projectId) {
      this.browseDirectory("");
    }
  },
  mounted() {
    document.addEventListener("click", this.handleOutsideClick);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleOutsideClick);
  },
  watch: {
    projectId: {
      handler(newProjectId) {
        if (newProjectId) {
          this.browseDirectory("");
        }
      },
      immediate: false,
    },
  },
};
</script>

<style scoped>
.container {
  max-width: 1120px;
  margin: auto;
}

h1,
h2 {
  color: #333;
}

table {
  border-collapse: collapse;
}

th,
td {
  padding: 1rem;
}

tr:nth-child(even) {
  background-color: #f9f9f9;
}

button {
  font-weight: 500;
}
</style>
